import React, { useState, useEffect, useRef } from "react";
import {
  TextField,
  InputAdornment,
  Paper,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  CircularProgress,
  IconButton,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import FlightIcon from "@mui/icons-material/Flight";
import SubdirectoryArrowRightIcon from "@mui/icons-material/SubdirectoryArrowRight";
import CloseIcon from "@mui/icons-material/Close";
import { searchAirportAutocomplete } from "../operations/airportService";

// Static user ID and radius
const userId = "4934da0c-5b17-4618-a41a-3d165b055df3";
const radius = "100 miles";

const AirportSearch: React.FC = () => {
  const [search, setSearch] = useState<string>("");
  const [loading, setLoading] = useState<boolean>(false);
  const [results, setResults] = useState<any[]>([]);
  const [manualSelect, setManualSelect] = useState<boolean>(false);
  const listRef = useRef<HTMLUListElement | null>(null);

  // ------------------ Fetch Airports ------------------
  const fetchAirports = async (query: string) => {
    if (!query) {
      setResults([]);
      return;
    }

    setLoading(true);
    try {
      const payload = {
        cities: [
          {
            name: query.toUpperCase(),
            latitude: null,
            longitude: null,
          },
        ],
        radius: radius,
        userId: userId,
      };
      const res = await searchAirportAutocomplete(payload);
      const data = res?.data?.data || [];
      setResults(data);
    } catch (err) {
      console.error("Error fetching airports:", err);
      setResults([]);
    }
    setLoading(false);
  };

  // ------------------ Debounce Typing ------------------
  useEffect(() => {
    if (manualSelect) return;
    const delayDebounce = setTimeout(() => {
      if (search) {
        fetchAirports(search);
      } else {
        setResults([]);
      }
    }, 500);
    return () => clearTimeout(delayDebounce);
  }, [search]);

  // ------------------ Handle Select ------------------
  const handleSelect = (label: string) => {
    setSearch(label);
    setManualSelect(true);
    setResults([]);
  };

  // ------------------ Render ------------------
  return (
    <div style={{ position: "relative", maxWidth: 600, margin: "0 auto" }}>
      {/* Search Input */}
      <TextField
        fullWidth
        placeholder="Search by city or airport"
        variant="outlined"
        value={search}
        onChange={(e) => {
          setManualSelect(false);
          setSearch(e.target.value);
        }}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon color="action" />
            </InputAdornment>
          ),
          endAdornment: search && (
            <InputAdornment position="end">
              <IconButton
                onClick={() => {
                  setSearch("");
                  setResults([]);
                  setManualSelect(false);
                }}
              >
                <CloseIcon />
              </IconButton>
            </InputAdornment>
          ),
        }}
      />

      {/* Dropdown */}
      {!manualSelect && search && (
        <Paper
          style={{
            marginTop: "0.5rem",
            maxHeight: 400,
            overflowY: "auto",
          }}
        >
          {loading ? (
            <div style={{ textAlign: "center", padding: "1rem" }}>
              <CircularProgress size={24} />
            </div>
          ) : results.length > 0 ? (
            <List ref={listRef}>
              {results.map((cityItem, index) => (
                <React.Fragment key={index}>
                  {/* City */}
                  {/* <ListItemButton
                    onClick={() => handleSelect(cityItem.city)}
                    sx={{ backgroundColor: "#f7f7f7" }}
                  >
                    <ListItemIcon>
                      <FlightIcon />
                    </ListItemIcon>
                    <ListItemText primary={cityItem.city} />
                  </ListItemButton> */}

                  {/* Airports */}
                  {cityItem.airports.map((airport: any, subIndex: number) => {
                    if (airport.type === "group") {
                      return (
                        <React.Fragment key={`${index}-${subIndex}`}>
                          {/* Group */}
                          <ListItemButton
                            sx={{ pl: 4, backgroundColor: "#f7f7f7" }}
                            onClick={() => handleSelect(airport.title)}
                          >
                            <ListItemIcon>
                              <FlightIcon />
                            </ListItemIcon>
                            <ListItemText primary={airport.title} />
                          </ListItemButton>

                          {/* Sub-airports */}
                          {airport.airports?.map(
                            (a: any, subSubIndex: number) => (
                              <ListItemButton
                                key={`${index}-${subIndex}-${subSubIndex}`}
                                sx={{ pl: 6 }}
                                onClick={() => handleSelect(a.fullName)}
                              >
                                <ListItemIcon>
                                  <SubdirectoryArrowRightIcon fontSize="small" />
                                </ListItemIcon>
                                <ListItemText
                                  primary={a.fullName}
                                  secondary={`${a.countryName}${
                                    a.distance ? " – " + a.distance : ""
                                  }`}
                                />
                              </ListItemButton>
                            )
                          )}
                        </React.Fragment>
                      );
                    } else if (airport.type === "airport") {
                      return (
                        <ListItemButton
                          key={`${index}-${subIndex}`}
                          sx={{ pl: 4 }}
                          onClick={() => handleSelect(airport.fullName)}
                        >
                          <ListItemIcon>
                            <FlightIcon fontSize="small" />
                          </ListItemIcon>
                          <ListItemText
                            primary={airport.fullName}
                            secondary={`${airport.countryName}${
                              airport.distance ? " – " + airport.distance : ""
                            }`}
                          />
                        </ListItemButton>
                      );
                    }
                    return null;
                  })}
                </React.Fragment>
              ))}
            </List>
          ) : (
            <div
              style={{ textAlign: "center", padding: "1rem", color: "#888" }}
            >
              No results found
            </div>
          )}
        </Paper>
      )}
    </div>
  );
};

export default AirportSearch;
